import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  UpdateGpsConfigurationResponse,
  updateGpsSettings,
} from './updateGpsSettings'
import { GpsAndBorderCrossingConfigurationModel } from './types'
import { COMPANY_API_SETTINGS_MOCKED } from '@/env'
import { QUERY_KEY_SETTINGS_GPS } from '@/lib/api/constants'
import { AxiosResponse } from 'axios'
import { toast } from '@/lib/toasts/api'

function getResponseMessage(
  response:
    | AxiosResponse<UpdateGpsConfigurationResponse>
    | UpdateGpsConfigurationResponse
    | undefined,
  fallback = '',
): string {
  if (!response) return fallback

  // AxiosResponse
  if ('data' in response && 'status' in response) {
    return response.data?.data?.message ?? fallback
  }

  // Normal API response
  return response.data?.message ?? fallback
}

export function useMutationGpsSettings(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<UpdateGpsConfigurationResponse>, // response
    Error, // error
    GpsAndBorderCrossingConfigurationModel // payload
  >({
    mutationFn: async (payload: GpsAndBorderCrossingConfigurationModel) => {
      if (COMPANY_API_SETTINGS_MOCKED) {
        return {
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {},
          data: { success: true, data: { message: 'Success' } },
        } as AxiosResponse<UpdateGpsConfigurationResponse>
      }

      const response = await updateGpsSettings({ companyId, payload })

      if (response.status !== 200) {
        const message = getResponseMessage(response)
        throw new Error(message || 'Request failed')
      }

      return response
    },

    onError: (error) => {
      toast.error({
        text: `Failed to save configuration.\nDetails. ${error.message}`,
      })
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_SETTINGS_GPS, companyId] })
      toast.success({ text: 'Configuration saved successfully.' })
    },
  })
}
